import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, QrCode, Radio, CheckCircle2, CalendarClock, X, Link2, Plus, UserPlus, ExternalLink, Info, MapPin } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { getInterviewSessions, getSessionRoster, getEmployerLink, getAssignableCandidates, createSession, assignCandidate, handoffUrl, applyShortUrl, onsiteShortUrl, supervisorShortUrl, PUBLIC_TOKEN, listSpecialSessions, createSpecialSession, updateSpecialSession, deleteSpecialSession } from "@/lib/api";
import { toast } from "sonner";

function StateBadge({ status }) {
  const map = { upcoming: ["registered", "Upcoming"], live: ["waiting", "Live Interview"], completed: ["approved", "Completed"] };
  const [v, label] = map[status] || ["registered", status];
  return <span className={`pill ${v}`}>{status === "live" && <Radio size={12} />}{label}</span>;
}

export default function InterviewSessions() {
  const [rows, setRows] = useState(null);
  const [link, setLink] = useState(null);
  const [assignable, setAssignable] = useState([]);
  const [qr, setQr] = useState(false);
  const [pubQr, setPubQr] = useState(false);
  const [onsiteQr, setOnsiteQr] = useState(false);
  const [roster, setRoster] = useState({});
  const [newOpen, setNewOpen] = useState(false);
  const [picked, setPicked] = useState({});
  const [specials, setSpecials] = useState([]);
  const [spOpen, setSpOpen] = useState(false);
  const [sp, setSp] = useState({ interviewDate: "", startTime: "10:00 AM", locationName: "Hope's Cookies", locationAddress: "", shiftNeed: "", capacity: "", candidateInstructions: "", active: true });
  const [editInstr, setEditInstr] = useState(null);
  const [spEdit, setSpEdit] = useState(null);
  const [spDelete, setSpDelete] = useState(null);

  const refresh = () => {
    getInterviewSessions().then(setRows).catch(() => {});
    getEmployerLink().then(setLink).catch(() => {});
    getAssignableCandidates().then(setAssignable).catch(() => {});
    listSpecialSessions().then(setSpecials).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);
  if (!rows) return <div className="empty" data-testid="sessions-loading">Loading interview sessions…</div>;

  const permanentUrl = link ? (link.token === PUBLIC_TOKEN ? supervisorShortUrl() : handoffUrl(link.token)) : "";
  const publicRegistrationUrl = applyShortUrl();
  const onsiteRegistrationUrl = onsiteShortUrl();
  const hasLive = rows.some((s) => s.status === "live");
  const pickedCount = Object.values(picked).filter(Boolean).length;

  const copyPermanent = () => { navigator.clipboard?.writeText(permanentUrl); toast.success("Permanent employer link copied"); };
  const copyPublic = () => { navigator.clipboard?.writeText(publicRegistrationUrl); toast.success("Public registration link copied"); };
  const copyOnsite = () => { navigator.clipboard?.writeText(onsiteRegistrationUrl); toast.success("On-site registration link copied"); };
  const toggleRoster = async (s) => {
    const cur = roster[s.id];
    if (cur?.open) { setRoster((r) => ({ ...r, [s.id]: { ...cur, open: false } })); return; }
    const data = await getSessionRoster(s.id);
    setRoster((r) => ({ ...r, [s.id]: { open: true, data } }));
  };
  const startNew = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (ids.length === 0) { toast.error("Select at least one candidate to assign"); return; }
    try {
      await createSession({ candidateIds: ids });
      toast.success("New interview session activated");
      setNewOpen(false); setPicked({}); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create session"); }
  };
  const doAssign = async (sid, regId) => {
    if (!regId) return;
    try { await assignCandidate(sid, regId); toast.success("Candidate assigned to session"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign candidate"); }
  };
  const createSpecial = async () => {
    if (!sp.interviewDate || !sp.startTime) { toast.error("Interview date and start time are required."); return; }
    try {
      await createSpecialSession({
        interviewDate: sp.interviewDate, startTime: sp.startTime.trim(),
        locationName: sp.locationName.trim() || null, locationAddress: sp.locationAddress.trim() || null,
        shiftNeed: sp.shiftNeed.trim() || null,
        candidateInstructions: sp.candidateInstructions.trim() || null,
        capacity: sp.capacity === "" ? null : Number(sp.capacity), active: sp.active,
      });
      toast.success("Special On-Site Interview created");
      setSpOpen(false);
      setSp({ interviewDate: "", startTime: "10:00 AM", locationName: "Hope's Cookies", locationAddress: "", shiftNeed: "", capacity: "", candidateInstructions: "", active: true });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create special session"); }
  };
  const toggleSpecial = async (s, active) => {
    try { await updateSpecialSession(s.id, { active }); toast.success(active ? "Now visible on public registration" : "Removed from public registration"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update session"); }
  };
  const closeSpecial = async (s) => {
    try { await updateSpecialSession(s.id, { status: "closed" }); toast.success("Special session closed"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not close session"); }
  };
  const confirmDelete = async () => {
    const s = spDelete;
    try {
      if (s.registered > 0) {
        await updateSpecialSession(s.id, { status: "cancelled" });
        toast.success("Session cancelled & removed from public — candidate history preserved");
      } else {
        await deleteSpecialSession(s.id);
        toast.success("Special On-Site Interview deleted");
      }
      setSpDelete(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove session"); }
  };
  const saveInstr = async () => {
    try {
      await updateSpecialSession(editInstr.id, { candidateInstructions: editInstr.text });
      toast.success("Candidate instructions saved");
      setEditInstr(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save instructions"); }
  };
  const saveEdit = async () => {
    if (!spEdit.interviewDate || !spEdit.startTime) { toast.error("Interview date and start time are required."); return; }
    try {
      await updateSpecialSession(spEdit.id, {
        displayName: spEdit.displayName, interviewDate: spEdit.interviewDate,
        startTime: spEdit.startTime, locationName: spEdit.locationName,
        locationAddress: spEdit.locationAddress, shiftNeed: spEdit.shiftNeed,
        capacity: spEdit.capacity === "" ? null : Number(spEdit.capacity),
        candidateInstructions: spEdit.candidateInstructions,
      });
      toast.success("Special On-Site Interview updated");
      setSpEdit(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update session"); }
  };

  return (
    <div data-testid="interview-sessions">
      <div className="crumb"><Link to="/dashboard">Staffing Home</Link> › <strong>Interview Sessions</strong></div>

      <div className="panel link-card" data-testid="permanent-link-card" style={{ marginBottom: 18 }}>
        <div className="ph link-card-ph">
          <span className="link-card-icon"><Link2 size={18} /></span>
          <div className="link-card-head">
            <h2>Permanent Employer Link</h2>
            <span className="link-card-kicker">Employer / supervisor interview handoff</span>
          </div>
          <span className="sp" />
          <StateBadgePermanent code={link?.activeSessionCode} />
        </div>
        <div className="pb">
          <p className="mut" style={{ marginBottom: 12 }}>Employers bookmark this one link. It always opens the employer's current active interview session and shows "No active session" when none is running — the URL never changes between sessions.</p>
          <div className="permalink-url" data-testid="permanent-link-url">{permanentUrl}</div>
          <div className="action-row" style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={copyPermanent} data-testid="copy-permanent-link"><Copy size={16} /> Copy Employer Link</button>
            <button className="btn" onClick={() => setQr(true)} data-testid="show-permanent-qr"><QrCode size={16} /> Show / Download QR</button>
            <a className="btn ghost" href={permanentUrl} target="_blank" rel="noreferrer" data-testid="open-permanent-link">Open Employer View</a>
            <span className="sp" />
            <button className="btn" onClick={() => document.getElementById("special-sessions")?.scrollIntoView({ behavior: "smooth", block: "start" })} data-testid="manage-special-sessions"><Plus size={16} /> Manage Special On-Site Interviews</button>
            <button className="btn primary" onClick={() => { setPicked({}); setNewOpen(true); }} disabled={hasLive} data-testid="start-new-session"><Plus size={16} /> Start Same-Day Live Session</button>
          </div>
          {hasLive && <div className="mut" style={{ marginTop: 8, fontSize: ".8125rem" }}>Complete the active session before starting a new one.</div>}
          {link?.activeSessionCode && (
            <details className="session-details" data-testid="permanent-session-details">
              <summary><Info size={13} style={{ verticalAlign: "-2px" }} /> Session details</summary>
              <div className="session-details-body">System reference · Session ID: <span className="quick-id" data-testid="permanent-session-id">{link.activeSessionCode}</span></div>
            </details>
          )}
        </div>
      </div>

      <div className="panel link-card link-card-alt" data-testid="public-registration-card" style={{ marginBottom: 18 }}>
        <div className="ph link-card-ph">
          <span className="link-card-icon"><UserPlus size={18} /></span>
          <div className="link-card-head">
            <h2>Public Interview Registration</h2>
            <span className="link-card-kicker">Candidate / recruiting-partner front door</span>
          </div>
          <span className="sp" />
          <span className="pill registered" data-testid="public-link-badge">Candidate link</span>
        </div>
        <div className="pb">
          <p className="mut" style={{ marginBottom: 12 }}>Share this link with <strong>candidates, recruiting partners, CareerLink, recovery/halfway houses, and other referral sources</strong>. Anyone who registers is matched to their existing canonical Candidate Record (or a new one is created), appears in the Candidate Pool, and joins the interview session they select. This is the candidate front door — it is <strong>not</strong> the employer/supervisor handoff link above.</p>
          <div className="permalink-url" data-testid="public-link-url">{publicRegistrationUrl}</div>
          <div className="action-row" style={{ marginTop: 12 }}>
            <a className="btn primary" href={publicRegistrationUrl} target="_blank" rel="noreferrer" data-testid="open-registration-form"><ExternalLink size={16} /> Open Registration Form</a>
            <button className="btn" onClick={copyPublic} data-testid="copy-registration-link"><Copy size={16} /> Copy Registration Link</button>
            <button className="btn" onClick={() => setPubQr(true)} data-testid="show-registration-qr"><QrCode size={16} /> Show / Download QR</button>
          </div>
        </div>
      </div>

      <div className="panel link-card link-card-onsite" data-testid="onsite-registration-card" style={{ marginBottom: 18 }}>
        <div className="ph link-card-ph">
          <span className="link-card-icon"><MapPin size={18} /></span>
          <div className="link-card-head">
            <h2>On-Site Interview Registration</h2>
            <span className="link-card-kicker">Controlled same-day / worksite doorway</span>
          </div>
          <span className="sp" />
          <span className="pill registered" data-testid="onsite-card-badge">Same-day link</span>
        </div>
        <div className="pb">
          <p className="mut" style={{ marginBottom: 12 }}>For candidates who are <strong>physically present at the worksite</strong> and are being considered for an immediate/same-day interview. Same-day intake must be <strong>opened by a supervisor</strong> (from the Employer Handoff view) before this registration can enter the live interview queue — while it is closed, candidates are routed to the public upcoming-interview form instead.</p>
          <div className="permalink-url" data-testid="onsite-link-url">{onsiteRegistrationUrl}</div>
          <div className="action-row" style={{ marginTop: 12 }}>
            <a className="btn primary" href={onsiteRegistrationUrl} target="_blank" rel="noreferrer" data-testid="open-onsite-form"><ExternalLink size={16} /> Open On-Site Registration Form</a>
            <button className="btn" onClick={copyOnsite} data-testid="copy-onsite-link"><Copy size={16} /> Copy On-Site Link</button>
            <button className="btn" onClick={() => setOnsiteQr(true)} data-testid="show-onsite-qr"><QrCode size={16} /> Show / Download QR</button>
          </div>
        </div>
      </div>

      <div className="panel link-card link-card-onsite" id="special-sessions" data-testid="special-sessions-card" style={{ marginBottom: 18 }}>
        <div className="ph link-card-ph">
          <span className="link-card-icon"><CalendarClock size={18} /></span>
          <div className="link-card-head">
            <h2>Special On-Site Interviews</h2>
            <span className="link-card-kicker">Manual one-off sessions — shown on public registration while Active</span>
          </div>
          <span className="sp" />
          <button className="btn primary" onClick={() => setSpOpen(true)} data-testid="add-special-session"><Plus size={16} /> Add Special Interview Session</button>
        </div>
        <div className="pb">
          <p className="mut" style={{ marginBottom: 12 }}>Create an urgent interview date (e.g. a Wednesday First-Shift push) without waiting for the recurring Monday. Active sessions appear as a selectable option on the Hope's Cookies registration page; the recurring Monday is never replaced.</p>
          {specials.length === 0 ? (
            <div className="mut" data-testid="special-empty">No special sessions yet.</div>
          ) : (
            <table data-testid="special-list">
              <thead><tr><th>Session</th><th>Date / Time</th><th>Location</th><th>Shift</th><th>Registered</th><th>Public</th><th>Actions</th></tr></thead>
              <tbody>
                {specials.map((s) => (
                  <tr key={s.id} data-testid={`special-row-${s.id}`}>
                    <td>{s.displayName}<div className="mut">{s.sessionCode}</div></td>
                    <td className="mut">{s.interviewDateLabel}<div>{s.startTime}</div></td>
                    <td className="mut">{s.locationName || "—"}{s.locationAddress ? <div>{s.locationAddress}</div> : null}</td>
                    <td className="mut">{s.shiftNeed || "—"}</td>
                    <td className="mut">{s.registered}{s.capacity != null ? ` / ${s.capacity}` : ""}{s.availableSpots != null ? ` · ${s.availableSpots} open` : ""}</td>
                    <td>{s.isPublic ? <span className="pill approved" data-testid={`special-public-${s.id}`}>Live on public</span> : <span className="pill" data-testid={`special-hidden-${s.id}`}>Hidden</span>}</td>
                    <td>
                      <div className="action-row">
                        <button className="btn sm" onClick={() => setSpEdit({ id: s.id, displayName: s.displayName || "Special On-Site Interview", interviewDate: s.interviewDate, startTime: s.startTime || "", locationName: s.locationName || "", locationAddress: s.locationAddress || "", shiftNeed: s.shiftNeed || "", capacity: s.capacity == null ? "" : s.capacity, candidateInstructions: s.candidateInstructions || "" })} data-testid={`special-edit-${s.id}`}>Edit</button>
                        <button className="btn sm" onClick={() => setEditInstr({ id: s.id, text: s.candidateInstructions || "" })} data-testid={`special-edit-instr-${s.id}`}>Edit instructions</button>
                        {s.status !== "closed" && s.publicAvailable
                          ? <button className="btn sm" onClick={() => toggleSpecial(s, false)} data-testid={`special-hide-${s.id}`}>Make unavailable</button>
                          : s.status !== "closed" ? <button className="btn sm" onClick={() => toggleSpecial(s, true)} data-testid={`special-show-${s.id}`}>Make available</button> : null}
                        {s.status !== "closed" && <button className="btn sm danger-outline" onClick={() => closeSpecial(s)} data-testid={`special-close-${s.id}`}>Close</button>}
                        {s.status === "closed" && <span className="mut">Closed</span>}
                        <button className="btn sm danger-outline" onClick={() => setSpDelete(s)} data-testid={`special-delete-${s.id}`}>Delete</button>
                        <a className="btn sm ghost" href={`/handoff/${s.token}`} target="_blank" rel="noreferrer" data-testid={`special-handoff-${s.id}`}>Employer view</a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {rows.map((s) => (
        <div className="panel" key={s.id} data-testid={`session-${s.sessionCode}`} style={{ marginBottom: 16 }}>
          <div className="ph session-ph">
            <CalendarClock size={18} style={{ opacity: .7, marginTop: 2 }} />
            <div className="session-head">
              <h2 className="session-title" data-testid={`session-title-${s.sessionCode}`}>{s.employerName} — {s.interviewDateLabel}</h2>
              <span className="session-sub">{s.interviewTime} · <StateBadge status={s.status} /></span>
            </div>
            <span className="sp" />
            <span className="session-id-ref" data-testid={`session-id-${s.sessionCode}`}>Session ID: {s.sessionCode}</span>
          </div>
          <div className="pb">
            <div className="session-stats">
              <div><span className="k">Expected</span><span className="v">{s.candidatesExpected}</span></div>
              <div><span className="k">Completed</span><span className="v">{s.candidatesCompleted}</span></div>
              <div><span className="k">Hire Approved</span><span className="v">{s.hireCount}</span></div>
              <div><span className="k">Handoffs</span><span className="v">{(s.handoffs || []).length}</span></div>
            </div>
            <div className="action-row" style={{ marginTop: 16 }}>
              <a className="btn ghost" href={`/handoff/${s.token}`} target="_blank" rel="noreferrer" data-testid={`open-handoff-${s.sessionCode}`}>Open employer view</a>
              <button className="btn ghost" onClick={() => toggleRoster(s)} data-testid={`view-roster-${s.sessionCode}`}>View Registered Candidates · {s.candidatesExpected}</button>
            </div>
            {s.status !== "completed" && (
              <div className="assign-row" style={{ marginTop: 12 }}>
                <select className="assign-select" defaultValue="" data-testid={`assign-select-${s.sessionCode}`} onChange={(e) => { doAssign(s.id, e.target.value); e.target.value = ""; }}>
                  <option value="">+ Assign a candidate to this session…</option>
                  {assignable.map((a) => <option key={a.registrationId} value={a.registrationId}>{a.shortId} · {a.name}</option>)}
                </select>
              </div>
            )}
            {roster[s.id]?.open && (
              <div className="roster-wrap" data-testid={`roster-${s.sessionCode}`}>
                <table>
                  <thead><tr><th>ID</th><th>Candidate</th><th>Current status</th><th>Employer result</th><th>Recorded by</th></tr></thead>
                  <tbody>
                    {roster[s.id].data.candidates.length === 0 && (
                      <tr><td colSpan={5} className="mut">No candidates assigned yet.</td></tr>
                    )}
                    {roster[s.id].data.candidates.map((c) => (
                      <tr key={c.registrationId} data-testid={`roster-row-${c.registrationId}`}>
                        <td><span className="quick-id">{c.shortId}</span></td>
                        <td><Link className="cand-name" to={`/candidates/${c.registrationId}`} data-testid={`roster-link-${c.registrationId}`}>{c.name}</Link></td>
                        <td className="mut">{c.status}</td>
                        <td>{c.result ? <span className="pill approved">{c.result}</span> : <span className="mut">—</span>}</td>
                        <td className="mut">{c.recordedBy || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(s.handoffs || []).length > 0 && (
              <div className="mut" style={{ marginTop: 14, fontSize: ".8125rem" }}>
                <CheckCircle2 size={14} style={{ verticalAlign: "-2px", color: "var(--mihp-green)" }} /> Last handoff by {s.handoffs[s.handoffs.length - 1].submittedBy} · {s.handoffs[s.handoffs.length - 1].counts.updated} candidates updated
              </div>
            )}
          </div>
        </div>
      ))}

      {spDelete && (
        <div className="modal-scrim" onClick={() => setSpDelete(null)} data-testid="special-delete-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            {spDelete.registered > 0 ? (
              <>
                <div className="modal-head"><h2>Candidates are associated with this interview session</h2><button className="x" onClick={() => setSpDelete(null)}><X size={20} /></button></div>
                <div className="modal-body">
                  <p className="modal-intro" data-testid="delete-affected-count"><strong>{spDelete.registered}</strong> candidate{spDelete.registered > 1 ? "s are" : " is"} registered to <strong>{spDelete.displayName} — {spDelete.interviewDateLabel}</strong>{spDelete.capacity != null ? ` (${spDelete.registered}/${spDelete.capacity})` : ""}.</p>
                  <p className="mut">Removing this session will take it off public registration. Candidate and communication history will be preserved so affected candidates can be contacted. The session is set to <strong>Cancelled</strong> — its ID, candidate assignments, Registration Received history, issued Confirmation Codes, and communication history are all kept.</p>
                </div>
                <div className="modal-foot">
                  <button className="btn ghost" onClick={() => setSpDelete(null)}>Cancel</button>
                  <button className="btn primary danger-outline" onClick={confirmDelete} data-testid="delete-confirm-btn">Cancel Session &amp; Remove from Public</button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-head"><h2>Delete Special On-Site Interview?</h2><button className="x" onClick={() => setSpDelete(null)}><X size={20} /></button></div>
                <div className="modal-body">
                  <p className="modal-intro">This will permanently remove <strong>{spDelete.displayName} — {spDelete.interviewDateLabel}</strong> and remove it from public registration. This action cannot be undone.</p>
                </div>
                <div className="modal-foot">
                  <button className="btn ghost" onClick={() => setSpDelete(null)}>Cancel</button>
                  <button className="btn primary danger-outline" onClick={confirmDelete} data-testid="delete-confirm-btn">Delete Session</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {spEdit && (
        <div className="modal-scrim" onClick={() => setSpEdit(null)} data-testid="special-edit-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Edit Special On-Site Interview</h2><button className="x" onClick={() => setSpEdit(null)}><X size={20} /></button></div>
            <div className="modal-body">
              <p className="modal-intro">Content-only edits — never changes the session ID, public link, routing, or existing candidate assignments/history.</p>
              <label className="oa-lbl">Interview date<input type="date" className="ca-input" value={spEdit.interviewDate} onChange={(e) => setSpEdit({ ...spEdit, interviewDate: e.target.value })} data-testid="special-edit-date" /></label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Start time<input className="ca-input" value={spEdit.startTime} onChange={(e) => setSpEdit({ ...spEdit, startTime: e.target.value })} data-testid="special-edit-time" /></label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Location name<input className="ca-input" value={spEdit.locationName} onChange={(e) => setSpEdit({ ...spEdit, locationName: e.target.value })} data-testid="special-edit-locname" /></label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Interview address<input className="ca-input" value={spEdit.locationAddress} onChange={(e) => setSpEdit({ ...spEdit, locationAddress: e.target.value })} data-testid="special-edit-address" /></label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Shift / hiring need<input className="ca-input" value={spEdit.shiftNeed} onChange={(e) => setSpEdit({ ...spEdit, shiftNeed: e.target.value })} data-testid="special-edit-shift" /></label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Capacity<input type="number" min="1" className="ca-input" value={spEdit.capacity} onChange={(e) => setSpEdit({ ...spEdit, capacity: e.target.value })} placeholder="No limit" data-testid="special-edit-capacity" style={{ maxWidth: 160 }} /></label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Candidate instructions / what to bring<textarea className="ca-input" rows={4} value={spEdit.candidateInstructions} onChange={(e) => setSpEdit({ ...spEdit, candidateInstructions: e.target.value })} data-testid="special-edit-instr-text" /></label>
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={() => setSpEdit(null)}>Cancel</button>
              <button className="btn primary" onClick={saveEdit} data-testid="special-edit-save">Save changes</button>
            </div>
          </div>
        </div>
      )}

      {editInstr && (
        <div className="modal-scrim" onClick={() => setEditInstr(null)} data-testid="special-instr-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Candidate Instructions / What to Bring</h2><button className="x" onClick={() => setEditInstr(null)}><X size={20} /></button></div>
            <div className="modal-body">
              <p className="modal-intro">Shown to candidates when they select this session (registration + confirmation). These are candidate-facing — not internal staff notes. Leave empty to hide the section. This is a content-only edit — it never changes the session ID, public link, routing, or candidate assignments.</p>
              <textarea className="ca-input" rows={6} value={editInstr.text} onChange={(e) => setEditInstr({ ...editInstr, text: e.target.value })} placeholder="What to bring, arrival/check-in, dress or safety requirements, parking, documents requested, other notes…" data-testid="special-instr-text" />
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={() => setEditInstr(null)}>Cancel</button>
              <button className="btn primary" onClick={saveInstr} data-testid="special-instr-save">Save instructions</button>
            </div>
          </div>
        </div>
      )}

      {spOpen && (
        <div className="modal-scrim" onClick={() => setSpOpen(false)} data-testid="special-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Add Special On-Site Interview</h2><button className="x" onClick={() => setSpOpen(false)}><X size={20} /></button></div>
            <div className="modal-body">
              <p className="modal-intro">A one-off interview session for any date — appears on the public Hope's Cookies registration page while Active. Does not affect the recurring Monday interview.</p>
              <label className="oa-lbl">Interview date
                <input type="date" className="ca-input" value={sp.interviewDate} onChange={(e) => setSp({ ...sp, interviewDate: e.target.value })} data-testid="special-date" />
              </label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Start time
                <input className="ca-input" value={sp.startTime} onChange={(e) => setSp({ ...sp, startTime: e.target.value })} placeholder="10:00 AM" data-testid="special-time" />
              </label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Location name
                <input className="ca-input" value={sp.locationName} onChange={(e) => setSp({ ...sp, locationName: e.target.value })} data-testid="special-locname" />
              </label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Interview address
                <input className="ca-input" value={sp.locationAddress} onChange={(e) => setSp({ ...sp, locationAddress: e.target.value })} placeholder="221 King Manor Dr, King of Prussia, PA 19406" data-testid="special-address" />
              </label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Shift / hiring need (optional)
                <input className="ca-input" value={sp.shiftNeed} onChange={(e) => setSp({ ...sp, shiftNeed: e.target.value })} placeholder="First Shift" data-testid="special-shift" />
              </label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Capacity (optional)
                <input type="number" min="1" className="ca-input" value={sp.capacity} onChange={(e) => setSp({ ...sp, capacity: e.target.value })} placeholder="No limit" data-testid="special-capacity" style={{ maxWidth: 160 }} />
              </label>
              <label className="oa-lbl" style={{ marginTop: 10 }}>Candidate instructions / what to bring (optional)
                <textarea className="ca-input" rows={4} value={sp.candidateInstructions} onChange={(e) => setSp({ ...sp, candidateInstructions: e.target.value })} placeholder="What to bring, arrival/check-in, dress or safety requirements, parking, documents requested, other notes…" data-testid="special-instructions" />
              </label>
              <label className="ca-ack" style={{ marginTop: 12 }}>
                <input type="checkbox" checked={sp.active} onChange={(e) => setSp({ ...sp, active: e.target.checked })} data-testid="special-active" />
                <span>Active / available now (show on public registration)</span>
              </label>
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={() => setSpOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={createSpecial} data-testid="special-submit">Create Special Session</button>
            </div>
          </div>
        </div>
      )}

      {newOpen && (
        <div className="modal-scrim" onClick={() => setNewOpen(false)} data-testid="new-session-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Start new interview session</h2><button className="x" onClick={() => setNewOpen(false)}><X size={20} /></button></div>
            <div className="modal-body">
              <p className="modal-intro">Select the candidates you are intentionally assigning to this employer session. Only assigned candidates appear on the employer handoff — new registrations never join automatically.</p>
              <div className="assign-list" data-testid="new-session-candidates">
                {assignable.length === 0 && <div className="mut">No candidates in Interview Pending.</div>}
                {assignable.map((a) => (
                  <label key={a.registrationId} className="assign-item" data-testid={`pick-${a.registrationId}`}>
                    <input type="checkbox" checked={!!picked[a.registrationId]} onChange={(e) => setPicked((p) => ({ ...p, [a.registrationId]: e.target.checked }))} />
                    <span className="quick-id">{a.shortId}</span> <span className="nm">{a.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={() => setNewOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={startNew} data-testid="create-session-submit">Activate session · {pickedCount}</button>
            </div>
          </div>
        </div>
      )}

      {qr && (
        <div className="modal-scrim" onClick={() => setQr(false)} data-testid="qr-modal">
          <div className="modal" style={{ maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Permanent employer QR</h2><button className="x" onClick={() => setQr(false)}><X size={20} /></button></div>
            <div className="modal-body" style={{ textAlign: "center" }}>
              <div style={{ background: "#fff", padding: 16, borderRadius: 12, display: "inline-block" }}>
                <QRCodeCanvas id="qr-canvas" value={permanentUrl} size={220} includeMargin />
              </div>
              <p className="mut" style={{ marginTop: 12, fontSize: ".8125rem" }}>Always resolves to the current active interview session.</p>
              <button className="btn primary" style={{ marginTop: 12 }} onClick={() => {
                const c = document.getElementById("qr-canvas"); const a = document.createElement("a");
                a.href = c.toDataURL("image/png"); a.download = `hopes-cookies-employer-qr.png`; a.click();
              }}>Download QR</button>
            </div>
          </div>
        </div>
      )}

      {onsiteQr && (
        <div className="modal-scrim" onClick={() => setOnsiteQr(false)} data-testid="onsite-qr-modal">
          <div className="modal" style={{ maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>On-site registration QR</h2><button className="x" onClick={() => setOnsiteQr(false)}><X size={20} /></button></div>
            <div className="modal-body" style={{ textAlign: "center" }}>
              <div style={{ background: "#fff", padding: 16, borderRadius: 12, display: "inline-block" }}>
                <QRCodeCanvas id="onsite-qr-canvas" value={onsiteRegistrationUrl} size={220} includeMargin />
              </div>
              <p className="mut" style={{ marginTop: 12, fontSize: ".8125rem" }}>Post at the worksite. Candidates scan to check in for a same-day interview when a supervisor has opened registration.</p>
              <button className="btn primary" style={{ marginTop: 12 }} onClick={() => {
                const c = document.getElementById("onsite-qr-canvas"); const a = document.createElement("a");
                a.href = c.toDataURL("image/png"); a.download = `hopes-cookies-onsite-qr.png`; a.click();
              }}>Download QR</button>
            </div>
          </div>
        </div>
      )}

      {pubQr && (
        <div className="modal-scrim" onClick={() => setPubQr(false)} data-testid="public-qr-modal">
          <div className="modal" style={{ maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Public registration QR</h2><button className="x" onClick={() => setPubQr(false)}><X size={20} /></button></div>
            <div className="modal-body" style={{ textAlign: "center" }}>
              <div style={{ background: "#fff", padding: 16, borderRadius: 12, display: "inline-block" }}>
                <QRCodeCanvas id="pub-qr-canvas" value={publicRegistrationUrl} size={220} includeMargin />
              </div>
              <p className="mut" style={{ marginTop: 12, fontSize: ".8125rem" }}>Candidates scan to open the public interview registration form.</p>
              <button className="btn primary" style={{ marginTop: 12 }} onClick={() => {
                const c = document.getElementById("pub-qr-canvas"); const a = document.createElement("a");
                a.href = c.toDataURL("image/png"); a.download = `hopes-cookies-registration-qr.png`; a.click();
              }}>Download QR</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StateBadgePermanent({ code }) {
  if (code) return <span className="pill waiting pill-active" data-testid="permanent-active-badge"><Radio size={12} />Active</span>;
  return <span className="pill registered" data-testid="permanent-none-badge">No active session</span>;
}
